import React, { useCallback, useMemo, useRef, useState, useEffect } from "react";
import PropTypes from "prop-types";
import IconButton from "../../atoms/IconButton";
import "../Composer/composer-v2.scss";
import { PostSaveIcon, PostCancelIcon } from "../../atoms/Iconography";
import RichTextInput from "../Composer/RichTextInput";
import FormattingToolbar from "../Composer/FormattingToolbar";
import useComposerFormatting from "../../../hooks/useComposerFormatting";

function extractTextFromHtml(html) {
  try {
    const container = document.createElement("div");
    container.innerHTML = String(html || "");
    return (container.textContent || "").replace(/\u00a0/g, " ").trim();
  } catch (_) {
    return String(html || "").replace(/<br\s*\/?>(?!\n)/gi, "\n").replace(/<[^>]*>/g, "").trim();
  }
}

export default function MessageInlineEditor({
  initialHtml,
  onCancel,
  onSave,
  autoFocus,
  ariaLabel,
  className,
  placeholder,
}) {
  const editorRef = useRef(null);
  const [html, setHtml] = useState(initialHtml || "");
  const [isSaving, setIsSaving] = useState(false);

  const { formatState, recomputeFormatState, applyWrapFormatting, applyCodeFormatting, applyQuoteFormatting, applyListFormatting, applyLinkFormatting } = useComposerFormatting(editorRef);

  useEffect(() => {
    if (autoFocus) {
      // Slight delay to ensure element is in DOM
      setTimeout(() => editorRef.current?.focus?.(), 0);
    }
  }, [autoFocus]);

  const canSave = useMemo(() => {
    const currentText = extractTextFromHtml(html);
    const initialText = extractTextFromHtml(initialHtml);
    if (!currentText) return false;
    return currentText !== initialText;
  }, [html, initialHtml]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onCancel?.();
      return;
    }
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault();
      if (canSave && !isSaving) {
        void (async () => {
          setIsSaving(true);
          try {
            await onSave?.(html);
          } finally {
            setIsSaving(false);
          }
        })();
      }
    }
  }, [canSave, html, isSaving, onCancel, onSave]);

  const handleSaveClick = useCallback(async () => {
    if (!canSave || isSaving) return;
    setIsSaving(true);
    try {
      await onSave?.(html);
    } finally {
      setIsSaving(false);
    }
  }, [canSave, html, isSaving, onSave]);

  return (
    <div className={`message-row__editor ${className || ""}`.trim()}>
      <div className="composer-v2" role="form" aria-label="Edit message composer">
        <FormattingToolbar
          onBold={() => applyWrapFormatting({ kind: 'bold' })}
          onItalic={() => applyWrapFormatting({ kind: 'italic' })}
          onStrike={() => applyWrapFormatting({ kind: 'strike' })}
          onLink={applyLinkFormatting}
          onOrdered={() => applyListFormatting({ ordered: true })}
          onBulleted={() => applyListFormatting({ ordered: false })}
          onQuote={applyQuoteFormatting}
          onCode={applyCodeFormatting}
          isBold={formatState.bold}
          isItalic={formatState.italic}
          isStrike={formatState.strike}
          isLink={formatState.link}
          isOrderedList={formatState.ordered}
          isBulletedList={formatState.bulleted}
          isQuote={formatState.quote}
          isCode={formatState.code}
        />

        <RichTextInput
          ref={editorRef}
          className="composer-v2__field"
          ariaLabel={ariaLabel || "Edit message"}
          valueHtml={html}
          onChangeHtml={setHtml}
          onKeyDown={handleKeyDown}
          onKeyUp={recomputeFormatState}
          onFocus={recomputeFormatState}
          onClick={recomputeFormatState}
          placeholder={placeholder || "Edit your message…"}
        />

        <div className="post-card__editor-actions" role="group" aria-label="Editing actions">
          <IconButton
            icon={<PostCancelIcon />}
            label="Cancel editing"
            variant="ghost"
            onClick={onCancel}
            disabled={isSaving}
            className="post-card__editor-button"
          />
          <IconButton
            icon={<PostSaveIcon />}
            label="Save message"
            variant="confirm"
            onClick={handleSaveClick}
            disabled={isSaving || !canSave}
            className="post-card__editor-button"
          />
        </div>
      </div>
    </div>
  );
}

MessageInlineEditor.propTypes = {
  initialHtml: PropTypes.string,
  onCancel: PropTypes.func,
  onSave: PropTypes.func,
  autoFocus: PropTypes.bool,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  placeholder: PropTypes.string,
};

MessageInlineEditor.defaultProps = {
  initialHtml: "",
  onCancel: undefined,
  onSave: undefined,
  autoFocus: true,
  ariaLabel: undefined,
  className: "",
  placeholder: undefined,
};


